import { Link } from 'react-router-dom';

interface TipCard {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  paragraphs: string[];
  externalLink: string;
}

const tips: TipCard[] = [
  {
    id: 'tire-tread-pro',
    title: 'Pro Guide: Tire Tread Depth',
    subtitle: 'Expert insights on measuring and maintaining optimal tread depth',
    image: '/tips/tire-tread-depth.svg',
    paragraphs: [
      'Tire tread depth is the most critical factor in determining your tire\'s safety and performance. New tires typically start with 10/32" to 11/32" of tread depth. The legal minimum in most states is 2/32", but safety experts recommend replacing tires at 4/32" for adequate wet weather performance. At 2/32", your stopping distance on wet roads can increase by over 100 feet at highway speeds.',
      'The penny test is a quick way to check tread depth: insert a penny into the tread groove with Lincoln\'s head facing down. If you can see the top of Lincoln\'s head, your tread is below 2/32" and needs immediate replacement. For a more accurate measurement, use a tread depth gauge or let TreadAI\'s computer vision analyze your tire photos for precise depth readings.',
      'Uneven tread wear patterns reveal important information about your vehicle. Center wear indicates over-inflation, while edge wear suggests under-inflation. One-sided wear points to alignment issues, and cupping or scalloping patterns often indicate worn suspension components. Regular rotation every 5,000-8,000 miles helps ensure even wear across all tires.',
      'Pro tip: Different tread depths across your tires can affect vehicle handling and safety systems like ABS and traction control. For AWD vehicles, manufacturers often recommend keeping tread depth variation under 2/32" between tires to prevent drivetrain damage. Always replace tires in pairs (front or rear) or as a complete set for optimal performance.'
    ],
    externalLink: 'https://www.nhtsa.gov/equipment/tires'
  },
  {
    id: 'tire-sizes',
    title: 'Understanding Tire Sizes',
    subtitle: 'Learn how to read the numbers on your tire sidewall',
    image: '/tips/tire-sidewall-markings.png',
    paragraphs: [
      'Every tire has a series of numbers and letters on its sidewall that tell you everything you need to know about that tire. For example, a marking like "P225/65R17 102H" contains crucial information: P indicates a passenger vehicle tire, 225 is the width in millimeters, 65 is the aspect ratio (sidewall height as a percentage of width), R means radial construction, and 17 is the wheel diameter in inches.',
      'The last numbers and letters indicate load index and speed rating. The load index (102 in our example) tells you the maximum weight each tire can support, while the speed rating (H) indicates the maximum speed the tire is designed to handle safely. Understanding these ratings ensures you choose tires appropriate for your vehicle and driving conditions.',
      'The DOT code on your tire also reveals its manufacturing date. The last four digits indicate the week and year of production - for example, "3219" means the tire was made in the 32nd week of 2019. Tires older than 6 years should be inspected regularly, and most manufacturers recommend replacement after 10 years regardless of tread depth.'
    ],
    externalLink: 'https://www.americastire.com/learn/check-tire-size'
  },
  {
    id: 'tire-calculator',
    title: 'Tire Size Calculator',
    subtitle: 'Compare sizes and find the right fit for your vehicle',
    image: '/tips/tire-calculator.svg',
    paragraphs: [
      'Choosing the right tire size is crucial for your vehicle\'s safety and performance. A tire size calculator helps you compare different tire sizes and understand how changes affect your speedometer accuracy, ground clearance, and overall vehicle handling. Even small changes in tire diameter can impact your odometer readings and fuel efficiency.',
      'When upgrading or changing tire sizes, the general rule is to stay within 3% of your original tire\'s overall diameter. Going too large can cause rubbing against fenders or suspension components, while tires that are too small affect speedometer accuracy and can reduce ground clearance. Plus-sizing (larger wheels with lower-profile tires) is popular for improved handling, but requires careful calculation.',
      'Modern tire calculators also help you understand the relationship between tire width, aspect ratio, and wheel diameter. A wider tire provides more grip but may affect fuel economy, while a lower aspect ratio improves handling response but can result in a firmer ride. Finding the right balance depends on your driving style and priorities.'
    ],
    externalLink: 'https://www.americastire.com/learn/tire-size-calculator'
  },
  {
    id: 'fuel-efficiency',
    title: 'Tires & Fuel Efficiency',
    subtitle: 'How your tires affect MPG and electric vehicle range',
    image: '/tips/fuel-efficiency.svg?v=2',
    paragraphs: [
      'Your tires have a significant impact on fuel economy - they can account for up to 20% of your vehicle\'s fuel consumption. This is due to rolling resistance, which is the energy required to keep your tires moving. Low rolling resistance tires are designed with special rubber compounds and tread patterns that reduce this energy loss, improving your miles per gallon.',
      'For electric vehicle owners, tire choice is even more critical. EVs are heavier due to their batteries, and every bit of range matters. Studies show that switching to EV-optimized tires can improve range by 10-15%. These tires feature reduced rolling resistance, reinforced sidewalls to handle the extra weight, and specially designed tread patterns for the instant torque that electric motors deliver.',
      'Proper tire maintenance also plays a huge role in efficiency. Under-inflated tires increase rolling resistance dramatically - for every 1 PSI drop in pressure, fuel economy decreases by about 0.2%. Regular pressure checks, proper alignment, and timely rotations not only extend tire life but also maximize your fuel savings. The EPA estimates that properly inflated tires can improve gas mileage by up to 3%.'
    ],
    externalLink: 'https://www.americastire.com/learn/treadwell-ev-range-and-fuel-efficiency'
  }
];

export const Tips = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-3xl p-8 border border-amber-200/50 shadow-xl overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-yellow-400/15 to-amber-400/15 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span className="text-sm font-semibold text-amber-700">Tire Knowledge Center</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Tire Tips & Guides
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about tire sizes, maintenance, and getting the most out of your tires.
            Learn from the experts and keep your vehicle running safely.
          </p>
        </div>
      </div>

      {/* Tip Cards */}
      <div className="grid gap-8">
        {tips.map((tip, index) => (
          <div
            key={tip.id}
            className="relative bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Image Section */}
              <div className={`relative bg-gradient-to-br from-gray-100 to-gray-200 p-8 flex items-center justify-center min-h-[280px] ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500 to-purple-600" />
                </div>
                <img
                  src={tip.image}
                  alt={tip.title}
                  className="relative z-10 w-full max-w-[380px] h-auto"
                  onError={(e) => {
                    // Fallback to placeholder if image doesn't load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="w-32 h-32 bg-gradient-to-br from-brand-500 to-purple-600 rounded-2xl flex items-center justify-center">
                        <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                    `;
                  }}
                />
              </div>

              {/* Content Section */}
              <div className={`p-8 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  {tip.id.includes('pro') ? (
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold rounded-full shadow-sm">
                      ⭐ Pro Tip
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-brand-100 text-brand-700 text-xs font-semibold rounded-full">
                      Tip #{index + 1}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{tip.title}</h2>
                <p className="text-brand-600 font-medium mb-4">{tip.subtitle}</p>

                <div className="space-y-4 text-gray-600 leading-relaxed">
                  {tip.paragraphs.map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <a
                    href={tip.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-brand-600 to-purple-600 text-white font-semibold rounded-xl hover:from-brand-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    Learn More
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Back to Dashboard */}
      <div className="text-center pt-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};
